<script setup lang="ts">
import { computed } from 'vue';
import { usePersonStore } from '@/stores/person';
import { formatTaxId } from '@/utils/formatters';
import { getInitials } from '@/utils/formatters';
import PersonTaxIdStatusBadge from '@/components/person/PersonTaxIdStatusBadge.vue';
import TruncatedText from '@/components/common/TruncatedText.vue';
import EntityMonitoringBadge from '@/components/entity-pulse/EntityMonitoringBadge.vue';
import type { EntityMonitoringView } from '@/composables/useEntityMonitoring';

const props = defineProps<{
  monitoring?: EntityMonitoringView | null;
}>();

const personStore = usePersonStore();

const personData = computed(() => personStore.generalData);

const initials = computed(() => {
    return getInitials(personData.value?.name);
});

// Auto-detect banner mode based on store state
const isInitialLoading = computed(() => 
    personStore.basicDataStatusLoading && !personStore.cpf
);

const hasCpf = computed(() => !!personStore.cpf);

const hasFullData = computed(() => 
    !!personStore.generalData && !personStore.generalData.error
);
</script>

<template>
    <!-- Skeleton mode: during initial load before we have CPF -->
    <div v-if="isInitialLoading" class="bg-[var(--p-primary-500)] text-white py-6 px-8 rounded-2xl mb-8 flex items-center w-full shadow-lg relative overflow-hidden">
        <div class="flex items-center justify-between gap-6 w-full">
            <div class="flex items-center gap-6 z-10">
                <!-- Skeleton avatar -->
                <div class="w-16 h-16 rounded-full bg-white/10 animate-pulse flex-shrink-0"></div>
                <div class="flex flex-col gap-2">
                    <!-- Skeleton name -->
                    <div class="w-48 h-6 rounded bg-white/10 animate-pulse"></div>
                    <!-- Skeleton details -->
                    <div class="w-72 h-4 rounded bg-white/10 animate-pulse"></div>
                </div>
            </div>
            <!-- Decoration -->
            <div class="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-0 text-[var(--p-primary-200)] opacity-50">
                <svg width="120" height="90" viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M40 80V20h50v60H40z M50 35h30 M50 50h30 M50 65h30" stroke-opacity="0.3"/>
                        <g transform="translate(10, 5)">
                            <circle cx="45" cy="35" r="20" stroke-opacity="0.7"/>
                            <line x1="60" y1="50" x2="75" y2="65" stroke-opacity="0.7" stroke-width="3"/>
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    </div>

    <!-- Lite mode: have CPF but waiting for full data -->
    <div v-else-if="hasCpf && !hasFullData" class="bg-[var(--p-primary-500)] text-white py-6 px-8 rounded-2xl mb-8 flex items-center w-full shadow-lg relative overflow-hidden">
        <div class="flex items-center justify-between gap-6 w-full">
            <div class="flex items-center gap-6 z-10">
                <div class="w-16 h-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center text-2xl font-semibold flex-shrink-0">
                    <span class="material-icons text-3xl opacity-90">person</span>
                </div>
                <div class="flex flex-col">
                    <h1 class="text-2xl font-semibold m-0 mb-1">Pessoa</h1>
                    <p class="text-sm text-[var(--p-primary-100)] m-0 flex items-center gap-2 opacity-90">
                        <span>CPF: {{ formatTaxId(personStore.cpf!) }}</span>
                    </p>
                </div>
            </div>
            <!-- Decoration -->
            <div class="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-0 text-[var(--p-primary-200)] opacity-50">
                <svg width="120" height="90" viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M40 80V20h50v60H40z M50 35h30 M50 50h30 M50 65h30" stroke-opacity="0.3"/>
                        <g transform="translate(10, 5)">
                            <circle cx="45" cy="35" r="20" stroke-opacity="0.7"/>
                            <line x1="60" y1="50" x2="75" y2="65" stroke-opacity="0.7" stroke-width="3"/>
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    </div>

    <!-- Full mode: complete banner with all person data -->
    <div v-else-if="hasFullData" class="bg-[var(--p-primary-500)] text-white py-6 px-8 rounded-2xl mb-8 flex items-center w-full shadow-lg relative overflow-hidden">
        <div class="flex items-center justify-between gap-6 w-full pr-32">
            <div class="flex items-center gap-6 z-10 flex-1 min-w-0">
                <div class="w-16 h-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center text-2xl font-semibold flex-shrink-0">
                    {{ initials }}
                </div>
                <div class="flex flex-col min-w-0">
                    <h1 class="text-2xl font-semibold m-0 mb-1">
                        <TruncatedText :text="personData!.name" fallback="Nome não informado" />
                    </h1>
                    <p class="text-sm text-white m-0 flex items-center gap-2 opacity-90">
                        <span>{{ formatTaxId(personData!.taxId) }}</span>
                        <span class="text-white/50">|</span>
                        <span>Situação do CPF:
                            <PersonTaxIdStatusBadge :status="personData!.taxIdStatus.description" />
                        </span>
                    </p>
                </div>
            </div>
            <!-- Monitored indicator: in flow so a long name truncates before reaching it; pr-32 keeps it clear of the decoration -->
            <div v-if="props.monitoring?.isMonitored" class="relative z-10 flex-shrink-0">
                <EntityMonitoringBadge :monitoring="props.monitoring" />
            </div>
            <!-- Decoration -->
            <div class="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-0 text-[var(--p-primary-200)] opacity-50">
                <svg width="120" height="90" viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M40 80V20h50v60H40z M50 35h30 M50 50h30 M50 65h30" stroke-opacity="0.3"/>
                        <g transform="translate(10, 5)">
                            <circle cx="45" cy="35" r="20" stroke-opacity="0.7"/>
                            <line x1="60" y1="50" x2="75" y2="65" stroke-opacity="0.7" stroke-width="3"/>
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    </div>
</template>
